import React, { useState, useEffect, useRef, useContext } from 'react'
import {
  Table,
  Button,
  Popconfirm,
  notification,
  Tag,
  Input,
  Space,
} from 'antd'
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  SearchOutlined,
  FileTextOutlined,
} from '@ant-design/icons'
import MemberModal from './MemberModal'
import { useMutation, useLazyQuery } from '@apollo/react-hooks'
import { GET_AVAILABILITY_LOGS } from '../graphql/Queries'
import {
  CREATE_TEAM_MEMBER,
  UPDATE_TEAM_MEMBER,
  DELETE_TEAM_MEMBER,
  CREATE_AVAILABILITY_LOG,
  UPDATE_AVAILABILITY_LOG,
} from '../graphql/Mutations'
import AvailabilityReasonModal from './AvailabilityReasonModal'
import AvailabilityLogsModal from './AvailabilityLogsModal'
import { Switch } from 'antd'
import { AppContext } from 'AppContext'
import { canUserEdit, canUserAdd, canUserDelete } from 'utils/User'

const PAGE_SIZE = 10

const MembersTable = ({ data, loading, refetch }) => {
  const { state: { currentUser, userPermissions, isClient } = {} } = useContext(AppContext) || {}
  const canEdit = canUserEdit(userPermissions || currentUser, isClient)
  const canAdd = canUserAdd(userPermissions || currentUser, isClient)
  const canDelete = canUserDelete(userPermissions || currentUser, isClient)

  const [reasonModalVisible, setReasonModalVisible] = useState(false)
  const [logsModalVisible, setLogsModalVisible] = useState(false)
  const [selectedMember, setSelectedMember] = useState(null)
  const [fetchLogs, { data: logsData, loading: logsLoading }] = useLazyQuery(
    GET_AVAILABILITY_LOGS
  )
  const [createTeamMember] = useMutation(CREATE_TEAM_MEMBER)
  const [updateTeamMember] = useMutation(UPDATE_TEAM_MEMBER)
  const [deleteTeamMember] = useMutation(DELETE_TEAM_MEMBER)
  const [createAvailabilityLog] = useMutation(CREATE_AVAILABILITY_LOG)
  const [updateLogReasonMutation, { loading: updating }] = useMutation(
    UPDATE_AVAILABILITY_LOG
  )
  const [modalVisible, setModalVisible] = useState(false)
  const [editingMember, setEditingMember] = useState(null)
  const [currentPage, setCurrentPage] = useState(1)
  const initialMembers = data?.getTeamMembers || data?.data?.getTeamMembers || []
  const [members, setMembers] = useState(initialMembers)
  const [searchedColumn, setSearchedColumn] = useState('')
  const searchInput = useRef(null)

  useEffect(() => {
    const list = data?.getTeamMembers || data?.data?.getTeamMembers
    if (list) {
      setMembers(list)
    }
  }, [data])

  const safeRefetch = async () => {
    if (typeof refetch === 'function') {
      try {
        const res = await refetch()
        const refreshedList =
          res?.data?.getTeamMembers || res?.data?.data?.getTeamMembers
        if (refreshedList) {
          setMembers(refreshedList)
        }
      } catch (err) {
        console.error('Error refetching team members:', err)
      }
    }
  }

  const handleAdd = () => {
    if (!canAdd) return
    setEditingMember(null)
    setModalVisible(true)
  }

  const handleEdit = (record) => {
    if (!canEdit) return
    setEditingMember(record)
    setModalVisible(true)
  }

  const handleDelete = async (id) => {
    if (!canDelete) return
    try {
      await deleteTeamMember({ variables: { id } })
      setMembers((prev) => prev.filter((m) => String(m.id) !== String(id)))
      notification.success({ message: 'Member deleted' })
      await safeRefetch()
    } catch {
      notification.error({ message: 'Delete failed' })
    }
  }

  const handleModalOk = async (values) => {
    if (editingMember && !canEdit) return
    if (!editingMember && !canAdd) return
    try {
      if (editingMember) {
        await updateTeamMember({
          variables: { id: editingMember.id, data: values },
        })
        notification.success({ message: 'Member updated' })
      } else {
        await createTeamMember({ variables: { data: values } })
        notification.success({ message: 'Member created' })
      }
      setModalVisible(false)
      await safeRefetch()
    } catch {
      notification.error({ message: 'Operation failed' })
    }
  }

  const handleToggleAvailability = async (record) => {
    if (!canEdit) return
    if (record.isAvailable) {
      setSelectedMember(record)
      setReasonModalVisible(true)
    } else {
      try {
        await updateTeamMember({
          variables: { id: record.id, data: { isAvailable: true } },
        })
        setMembers((prev) =>
          prev.map((m) =>
            String(m.id) === String(record.id) ? { ...m, isAvailable: true } : m
          )
        )
        notification.success({ message: 'Marked as Available' })
        await safeRefetch()
      } catch {
        notification.error({ message: 'Failed to update availability' })
      }
    }
  }

  const handleReasonSubmit = async (reason) => {
    if (!canEdit) return
    try {
      await updateTeamMember({
        variables: { id: selectedMember.id, data: { isAvailable: false } },
      })
      await createAvailabilityLog({
        variables: {
          input: {
            teamMemberId: selectedMember.id,
            previousAvailability: true,
            newAvailability: false,
            reason,
          },
        },
      })
      setMembers((prev) =>
        prev.map((m) =>
          String(m.id) === String(selectedMember.id)
            ? { ...m, isAvailable: false }
            : m
        )
      )
      notification.success({ message: 'Marked as Unavailable & Logged' })
      await safeRefetch()
    } catch {
      notification.error({ message: 'Failed to update availability' })
    } finally {
      setReasonModalVisible(false)
      setSelectedMember(null)
    }
  }

  const handleViewLogs = (member) => {
    setSelectedMember(member)
    setLogsModalVisible(true)
    fetchLogs({ variables: { teamMemberId: +member.id } })
  }

  const handleEditLogReason = async (logId, newReason) => {
    try {
      await updateLogReasonMutation({
        variables: { id: logId, data: { reason: newReason } },
      })
      await safeRefetch()
      if (selectedMember?.id) {
        fetchLogs({ variables: { teamMemberId: +selectedMember.id } })
      }
    } catch (err) {
      notification.error({ message: 'Failed to update reason' })
    }
  }

  const getColumnSearchProps = (dataIndex, customRender) => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
    }) => (
      <div style={{ padding: 8 }}>
        <Input
          ref={searchInput}
          placeholder={`Search ${dataIndex}`}
          value={selectedKeys[0]}
          onChange={(e) =>
            setSelectedKeys(e.target.value ? [e.target.value] : [])
          }
          onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex)}
          style={{ marginBottom: 8, display: 'block' }}
        />
        <Space>
          <Button
            type='primary'
            onClick={() => handleSearch(selectedKeys, confirm, dataIndex)}
            icon={<SearchOutlined />}
            size='small'
            style={{ width: 90 }}
          >
            Search
          </Button>
          <Button
            onClick={() => handleReset(clearFilters)}
            size='small'
            style={{ width: 90 }}
          >
            Reset
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered) => (
      <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />
    ),
    onFilter: (value, record) => {
      const target = customRender ? customRender(record) : record[dataIndex]
      return target?.toString().toLowerCase().includes(value.toLowerCase())
    },
    onFilterDropdownOpenChange: (visible) => {
      if (visible) {
        setTimeout(() => searchInput.current?.select(), 100)
      }
    },
    render: customRender
      ? (text, record) => customRender(record)
      : (text) => (searchedColumn === dataIndex ? <span>{text}</span> : text),
  })

  const handleSearch = (selectedKeys, confirm, dataIndex) => {
    confirm()
    setSearchedColumn(dataIndex)
  }

  const handleReset = (clearFilters) => {
    clearFilters()
  }

  const columns = [
    {
      title: 'Team',
      key: 'team',
      ...getColumnSearchProps('team', (record) => record.team?.teamName || ''),
    },
    {
      title: 'User',
      key: 'user',
      ...getColumnSearchProps(
        'user',
        (record) =>
          `${record.user?.firstName || ''} ${record.user?.lastName || ''}`
      ),
    },
    {
      title: 'Specialization',
      key: 'specialization',
      ...getColumnSearchProps(
        'specialization',
        (record) => record.specialization?.join(' ') || ''
      ),
      render: (_, record) => (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          {record?.specialization?.map((spec, index) => (
            <Tag key={index} color={'blue'} style={{ marginBottom: 4 }}>
              {spec}
            </Tag>
          ))}
        </div>
      ),
    },
    {
      title: 'Available',
      key: 'isAvailable',
      render: (_, record) => (
        <Switch
          checked={record.isAvailable}
          disabled={!canEdit}
          onChange={() => handleToggleAvailability(record)}
        />
      ),
    },
    {
      title: 'Current Ticket Load',
      dataIndex: 'ticketLoad',
      key: 'ticketLoad',
      sorter: (a, b) => (a.ticketLoad || 0) - (b.ticketLoad || 0),
      sortDirections: ['ascend', 'descend'],
    },
    {
      title: 'Member Load Capacity',
      dataIndex: 'maxTicketLoad',
      key: 'maxTicketLoad',
      sorter: (a, b) => (a.maxTicketLoad || 0) - (b.maxTicketLoad || 0),
      sortDirections: ['ascend', 'descend'],
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <>
          {canEdit && (
            <EditOutlined
              style={{ marginRight: 12, cursor: 'pointer' }}
              onClick={() => handleEdit(record)}
            />
          )}
          <FileTextOutlined
            style={{
              cursor: 'pointer',
              color: '#1890ff',
              marginRight: canDelete ? 12 : 0,
            }}
            onClick={() => handleViewLogs(record)}
          />
          {canDelete && (
            <Popconfirm
              title='Delete?'
              onConfirm={() => handleDelete(record.id)}
            >
              <DeleteOutlined style={{ cursor: 'pointer', color: 'red' }} />
            </Popconfirm>
          )}
        </>
      ),
    },
  ]

  const paginatedData = members.slice(
    (currentPage - 1) * PAGE_SIZE,
    currentPage * PAGE_SIZE
  )

  return (
    <div>
      <AvailabilityReasonModal
        visible={reasonModalVisible}
        onCancel={() => setReasonModalVisible(false)}
        onSubmit={handleReasonSubmit}
      />
      <AvailabilityLogsModal
        visible={logsModalVisible}
        onCancel={() => setLogsModalVisible(false)}
        logs={logsData?.teamMemberAvailabilityLogs || []}
        loading={logsLoading || updating}
        onEdit={handleEditLogReason}
      />
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <div style={{ fontWeight: 600, fontSize: 18 }}>Members settings</div>
        {canAdd && (
          <Button type='primary' icon={<PlusOutlined />} onClick={handleAdd}>
            Add Member
          </Button>
        )}
      </div>
      <Table
        columns={columns}
        dataSource={paginatedData}
        rowKey='id'
        loading={loading && !members.length}
        pagination={{
          current: currentPage,
          pageSize: PAGE_SIZE,
          total: members.length,
          onChange: setCurrentPage,
        }}
      />
      <MemberModal
        visible={modalVisible}
        onCancel={() => setModalVisible(false)}
        onOk={handleModalOk}
        editingMember={editingMember}
      />
    </div>
  )
}

export default MembersTable
